import React, { useState } from 'react';
import { useTranslation } from '../Context/TranslationContext';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';

const ContactPage = () => {
  const { isTamil } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const translations = {
    en: {
      title: "Contact Us",
      subtitle: "Get in touch with us",
      description: "We're here to help and answer any question you might have.",
      contactInfo: {
        title: "Contact Information",
        address: "State Headquarters, Chennai, Tamil Nadu",
        phone: "+91 XXXXX XXXXX",
        email: "example@bharatscouts.org",
        hours: "Mon - Fri: 9:00 AM - 5:00 PM"
      },
      form: {
        title: "Send us a message",
        name: "Your Name",
        email: "Email Address",
        phone: "Phone Number",
        subject: "Subject",
        message: "Your Message",
        button: "Send Message",
        success: "Message sent successfully!",
        error: "Error sending message. Please try again."
      }
    },
    ta: {
      title: "எங்களை தொடர்பு கொள்ள",
      subtitle: "எங்களுடன் தொடர்பில் இருங்கள்",
      description: "உங்களுக்கு இருக்கக்கூடிய எந்தக் கேள்விக்கும் உதவுவதற்கும் பதிலளிக்கவும் நாங்கள் இங்கே இருக்கிறோம்.",
      contactInfo: {
        title: "தொடர்பு விவரங்கள்",
        address: "மாநில தலைமையகம், சென்னை, தமிழ்நாடு",
        phone: "+91 XXXXX XXXXX",
        email: "example@bharatscouts.org",
        hours: "திங்கள் - வெள்ளி: காலை 9:00 - மாலை 5:00"
      },
      form: {
        title: "எங்களுக்கு செய்தி அனுப்பவும்",
        name: "உங்கள் பெயர்",
        email: "மின்னஞ்சல் முகவரி",
        phone: "தொலைபேசி எண்",
        subject: "பொருள்",
        message: "உங்கள் செய்தி",
        button: "செய்தி அனுப்பு",
        success: "செய்தி வெற்றிகரமாக அனுப்பப்பட்டது!",
        error: "செய்தி அனுப்புவதில் பிழை. மீண்டும் முயற்சிக்கவும்."
      }
    }
  };

  const t = translations[isTamil ? 'ta' : 'en'];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Add your form submission logic here
    console.log('Form submitted:', formData);
    // Reset form after submission
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header Section */}
      <div className="bg-blue-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold mb-4">{t.title}</h1>
          <p className="text-xl text-blue-100">{t.description}</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Contact Information */}
            <div className="bg-blue-800 text-white p-8">
              <h3 className="text-2xl font-semibold mb-8">{t.contactInfo.title}</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="w-6 h-6 mt-1" />
                  <p>{t.contactInfo.address}</p>
                </div>
                <div className="flex items-center space-x-4">
                  <Phone className="w-6 h-6" />
                  <p>{t.contactInfo.phone}</p>
                </div>
                <div className="flex items-center space-x-4">
                  <Mail className="w-6 h-6" />
                  <p>{t.contactInfo.email}</p>
                </div>
                <div className="flex items-center space-x-4">
                  <Clock className="w-6 h-6" />
                  <p>{t.contactInfo.hours}</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="md:col-span-2 p-8">
              <h3 className="text-2xl font-semibold mb-8">{t.form.title}</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.form.name}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.form.email}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.form.phone}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.form.subject}
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.form.message}
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  ></textarea>
                </div>
                <div>
                  <button
                    type="submit"
                    className="bg-blue-800 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-300 w-full md:w-auto"
                  >
                    {t.form.button}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;